import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { SHORT_TOUCH_MEDIA } from '../hooks/useIsPhone';
import { Box, Typography } from '@mui/material';
import { useTeamspace } from '../context/TeamspaceProvider';
import { useAuth } from '../context/AuthContext';
import CalendarComponent from '../components/CalendarComponent';
import { utcToZonedTime } from 'date-fns-tz';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { startOfWeek, endOfWeek, format, parseISO, isValid } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useTimeEntriesLiveRefresh } from '../hooks/useTimeEntriesLiveRefresh';
import { useStickyState } from '../hooks/useStickyState'; 
import { useCalendarFetch } from '../hooks/useCalendarFetch';
import { isRestrictedStaff, isSameMember } from '../config/staffRestrictions';

const CalendarPage = () => {
  const { user, role_dict } = useAuth();
  const {
    clients, projects, members, teamspaces, selectedTeamspaceId,
    displayTimezone, timezoneReady, optimisticCreateTimeEntry,
    startTimeEntryMutation, endTimeEntryMutation,
    optionsByTeamspace, ensureOptionsForTeamspace, teamspaceIdToNameMap,
  } = useTeamspace();

  // DCC Staff see only their own entries. The server enforces this per
  // teamspace on the time-entry queryset; here it keeps the member picker from
  // offering colleagues whose weeks would come back empty.
  const restrictedStaff = isRestrictedStaff(user, role_dict, teamspaceIdToNameMap?.[selectedTeamspaceId]);

  // Ensure member options are loaded for every teamspace so the member filter
  // dropdown always shows members across all teamspaces, not just the selected one.
  useEffect(() => {
    if (!teamspaces?.length) return;
    teamspaces.forEach(ts => {
      if (ts.id) ensureOptionsForTeamspace(ts.id);
    });
  }, [teamspaces, ensureOptionsForTeamspace]);

  // Build deduplicated members/projects lists across all cached teamspaces.
  // These are passed to CalendarComponent so its filter dropdowns show options
  // from every teamspace, not just the currently selected one.

  const allTeamspacesMembers = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const m of members) {
      const key = m.username || m.member_username || m.email || m.label;
      if (key && !seen.has(key)) { seen.add(key); combined.push(m); }
    }
    for (const cached of Object.values(optionsByTeamspace)) {
      for (const m of cached.members || []) {
        const key = m.username || m.member_username || m.email || m.label;
        if (key && !seen.has(key)) { seen.add(key); combined.push(m); }
      }
    }
    if (restrictedStaff) {
      return combined.filter((m) => isSameMember(m, user));
    }
    return combined;
  }, [members, optionsByTeamspace, restrictedStaff, user]);

  const allTeamspacesProjects = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const p of projects) {
      if (p.id != null && !seen.has(p.id)) { seen.add(p.id); combined.push(p); }
    }
    for (const cached of Object.values(optionsByTeamspace)) {
      for (const p of cached.projects || []) {
        if (p.id != null && !seen.has(p.id)) { seen.add(p.id); combined.push(p); }
      }
    }
    return combined;
  }, [projects, optionsByTeamspace]);

  // Per-teamspace project lookup derived from the pre-loaded optionsByTeamspace cache.
  // Passed to CalendarComponent so it can resolve project names for cross-teamspace entries
  // without making its own API calls.
  const projectsByTeamspace = useMemo(() => {
    const map = {};
    // Seed with the currently selected teamspace's projects (always loaded)
    if (selectedTeamspaceId) {
      const lookup = {};
      for (const p of projects) { lookup[String(p.id)] = p; }
      map[String(selectedTeamspaceId)] = lookup;
    }
    for (const [tsId, cached] of Object.entries(optionsByTeamspace)) {
      if (!cached.projects?.length) continue;
      const lookup = {};
      for (const p of cached.projects) { lookup[String(p.id)] = p; }
      map[String(tsId)] = lookup;
    }
    return map;
  }, [projects, optionsByTeamspace, selectedTeamspaceId]);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParamsRef = useRef(searchParams.toString());

  // const isInitialLoad = useRef(true);

  // // Unlock the calendar after 1 second so navigation starts working
  //   useEffect(() => {
  //     if (timezoneReady) {
  //       const timer = setTimeout(() => {
  //         isInitialLoad.current = false;
  //         console.log("🔓 Calendar guard released. Navigation now enabled.");
  //       }, 1500); // 1.5s is safer for slower connections
  //       return () => clearTimeout(timer);
  //     }
  //   }, [timezoneReady]);
  
  // --- 1. CONSISTENT DATE FORMATTING ---
  // We store the week start as a "YYYY-MM-DD" string. This is safe for 
  // URL params, Sticky State (JSON), and doesn't lose timezone info.
  
  const [weekStartStr, setWeekStartStr] = useStickyState(
    () => format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'), 
    'calendar_week_start'
  );

  // Convert to Date object only for logic/math
  const currentWeekStart = useMemo(() => {
    let date = parseISO(weekStartStr);
    
    // 2. If invalid, fallback to current week
    if (!isValid(date)) {
      date = startOfWeek(new Date(), { weekStartsOn: 1 });
    }

    // 3. CRITICAL: Set time to 12:00 (Noon).
    // This ensures that even if timezone subtracts 8-12 hours, 
    // it remains the SAME day.
    date.setHours(12, 0, 0, 0);
    
    return date;
  }, [weekStartStr]);

  const currentUserIdentifier = useMemo(() => user?.username || user?.email || '', [user]);

  const [selectedMember, setSelectedMember] = useStickyState(
    currentUserIdentifier, 
    'cal_filter_member'
  );
  // --- 2. FILTERS & WEBSOCKET ---
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [selectedClients, setSelectedClients] = useStickyState([], 'cal_filter_clients');
  const [selectedProjects, setSelectedProjects] = useStickyState([], 'cal_filter_projects');
  const [selectedBillable, setSelectedBillable] = useStickyState('all', 'cal_filter_billable');
  // const [selectedMember, setSelectedMember] = useStickyState('', 'cal_filter_member');
  const [selectedTeamspaces, setSelectedTeamspaces] = useStickyState([], 'cal_filter_teamspaces');
  const [wsUpdateTrigger, setWsUpdateTrigger] = useState(0);
  const backgroundRefreshRef = useRef(false);

  // Time-entry events only, debounced: client/project, invoicing and email-draft
  // events share this socket but never change the week's entries (project names
  // come from the TeamspaceProvider lookups, not from this fetch).
  useTimeEntriesLiveRefresh({
    teamspaceId: selectedTeamspaceId,
    allTeamspaces: true, // the calendar shows entries from every teamspace
    onRefresh: useCallback(() => {
      backgroundRefreshRef.current = true;
      setWsUpdateTrigger(v => v + 1);
    }, []),
  });

  useEffect(() => {
    if (filtersInitialized && !selectedMember && currentUserIdentifier) {
      setSelectedMember(currentUserIdentifier);
    }
  }, [filtersInitialized, selectedMember, currentUserIdentifier, setSelectedMember]);

  // Sticky state or a shared URL can leave another member selected. Restricted
  // Staff would then see an empty week, so snap the filter back to themselves.
  useEffect(() => {
    if (!restrictedStaff || !currentUserIdentifier) return;
    if (selectedMember && selectedMember !== currentUserIdentifier) {
      setSelectedMember(currentUserIdentifier);
    }
  }, [restrictedStaff, selectedMember, currentUserIdentifier, setSelectedMember]);

    const {
      entries: calendarTimeEntries,
      setEntries: setCalendarTimeEntries,
      loading: timeEntriesLoading
    } = useCalendarFetch({
      teamspaces,
      weekStart: currentWeekStart,
      timezone: displayTimezone,
      selectedMember, // Guaranteed to be something (self or specific choice)
      timezoneReady,
      refreshTrigger: wsUpdateTrigger,
      wsRefreshTrigger: wsUpdateTrigger,
      backgroundRefreshRef
    });

  // Grouped entries come from the server (`include_groups`, see useCalendarFetch):
  // each one carries its whole group, other members' entries included. Map every
  // member of every group -> that group, the shape CalendarComponent and the entry
  // forms have always received. This replaced a second, unfiltered fetch of
  // everyone's week that was capped at 200 rows and missed groups in busy weeks.
  const groupMap = useMemo(() => {
    const map = new Map();
    for (const entry of calendarTimeEntries) {
      if (!entry.group) continue;
      for (const member of entry.group) map.set(String(member.id), entry.group);
    }
    return map;
  }, [calendarTimeEntries]);

  // An entry by id: a visible one, or another member's entry inside a group.
  const findEntry = useCallback((id) => {
    const key = String(id);
    return calendarTimeEntries.find(e => String(e.id) === key)
      || groupMap.get(key)?.find(e => String(e.id) === key)
      || null;
  }, [calendarTimeEntries, groupMap]);

  // Groups are computed by the server, so after a change that can alter one -- a
  // grouped entry saved or moved, or an entry added/removed for another member (the
  // forms do that when a group's members change) -- refetch the week now rather than
  // after the debounced live refresh, so reopening the entry shows the current group.
  // Coalesced: moving a group saves each member's entry in turn.
  const groupRefreshTimerRef = useRef(null);
  const refreshGroupsSoon = useCallback(() => {
    clearTimeout(groupRefreshTimerRef.current);
    groupRefreshTimerRef.current = setTimeout(() => {
      backgroundRefreshRef.current = true;
      setWsUpdateTrigger(v => v + 1);
    }, 300);
  }, []);
  useEffect(() => () => clearTimeout(groupRefreshTimerRef.current), []);
  const mayChangeGroup = useCallback((entry) => (
    !entry
    || groupMap.has(String(entry.id))
    || !entry.member_username
    || entry.member_username !== selectedMember
  ), [groupMap, selectedMember]);

  // Listen for time entries added via AddTimeEntryForm (from FloatingAddButton)
  // Must be after useCalendarFetch to access setCalendarTimeEntries
  useEffect(() => {
    const handleTimeEntryAdded = (event) => {
      const newTimeEntry = event.detail?.timeEntry;
      if (!newTimeEntry) return;
      
      // Check if entry matches current filters (member, week range)
      const entryMemberUsername = newTimeEntry.member_username || newTimeEntry.member;
      const entryMatchesMember = !selectedMember || 
        entryMemberUsername === selectedMember ||
        entryMemberUsername === user?.username ||
        entryMemberUsername === user?.email;
      
      // Check if entry is in current week range (with padding for timezone conversion)
      const entryStart = newTimeEntry.start_time ? new Date(newTimeEntry.start_time) : null;
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const entryInWeek = entryStart && 
        // 1 day before week start (padding), from that day's midnight — same as useCalendarFetch.
        entryStart >= new Date(new Date(currentWeekStart.getTime() - 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0)) &&
        entryStart <= new Date(weekEnd.getTime() + 24 * 60 * 60 * 1000); // 1 day after week end (padding)
      
      // Ensure teamspace_id is a number (for color mapping)
      const teamspaceId = Number(newTimeEntry.teamspace || newTimeEntry.teamspace_id);
      const calendarEntry = {
        ...newTimeEntry,
        teamspace_id: teamspaceId,
        teamspace: teamspaceId,
        member_username: entryMemberUsername || newTimeEntry.member_username,
      };

      // A new entry can complete a group (same fields as another member's entry).
      if (entryInWeek) refreshGroupsSoon();

      // Only add to the visible calendar if the entry matches the member filter
      if (entryMatchesMember && entryInWeek) {
        setCalendarTimeEntries(prev => {
          const exists = prev.some(e =>
            String(e.id) === String(calendarEntry.id) ||
            (calendarEntry.id?.toString().startsWith('temp-') &&
             e.start_time === calendarEntry.start_time &&
             e.member_username === calendarEntry.member_username)
          );
          return exists ? prev : [...prev, calendarEntry];
        });
      }
    };

    const handleTimeEntryUpdatedFromApi = (event) => {
      const { tempId, realEntry } = event.detail || {};
      if (tempId && realEntry) {
        // Replace temporary entry with real entry from API
        const replacer = prev => {
          const index = prev.findIndex(entry => String(entry.id) === String(tempId));
          if (index !== -1) {
            const updated = [...prev];
            updated[index] = {
              ...realEntry,
              teamspace_id: realEntry.teamspace_id || realEntry.teamspace || Number(realEntry.teamspace),
            };
            return updated;
          }
          return prev;
        };
        setCalendarTimeEntries(replacer);
      }
    };

    window.addEventListener('time-entry-added', handleTimeEntryAdded);
    window.addEventListener('time-entry-updated-from-api', handleTimeEntryUpdatedFromApi);

    return () => {
      window.removeEventListener('time-entry-added', handleTimeEntryAdded);
      window.removeEventListener('time-entry-updated-from-api', handleTimeEntryUpdatedFromApi);
    };
  }, [selectedMember, currentWeekStart, user, setCalendarTimeEntries, refreshGroupsSoon]);
  
  // --- 4. URL & STICKY SYNC ---
  const updateURLParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (!value || value === 'all' || (Array.isArray(value) && !value.length)) {
        newParams.delete(key);
      } else if (Array.isArray(value)) {
        newParams.delete(key);
        value.forEach(v => newParams.append(key, v));
      } else {
        newParams.set(key, value);
      }
    });
    // Every navigation re-renders everything under the router, so skip no-ops. This
    // effect re-runs after its own write (searchParams is a dependency); without the
    // check that second run navigated again to the same URL.
    if (newParams.toString() === searchParams.toString()) return;
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync URL -> Local State
  useEffect(() => {
    if (!user || filtersInitialized) return;
    
    if (searchParams.has('week')) setWeekStartStr(searchParams.get('week'));
    if (searchParams.has('clients')) setSelectedClients(searchParams.getAll('clients'));
    if (searchParams.has('billable')) setSelectedBillable(searchParams.get('billable'));
    if (searchParams.has('member')) setSelectedMember(searchParams.get('member'));
    
    setFiltersInitialized(true);
  }, [user, searchParams, filtersInitialized, setWeekStartStr, setSelectedClients, setSelectedBillable, setSelectedMember]);

  // Sync Local State -> URL
  useEffect(() => {
    if (!filtersInitialized) return;
    updateURLParams({
      week: weekStartStr,
      clients: selectedClients,
      member: selectedMember,
      billable: selectedBillable
    });
  }, [weekStartStr, selectedClients, selectedMember, selectedBillable, filtersInitialized, updateURLParams]);

  // --- 5. TIMEZONE CONVERSION ---
  const convertedTimeEntries = useMemo(() => {
    if (!timezoneReady || !calendarTimeEntries?.length) return [];
    return calendarTimeEntries.map(entry => {
      // Store original UTC times before conversion (for form editing)
      const originalStartUTC = entry.start_time;
      const originalEndUTC = entry.end_time;
      
      const zonedStart = utcToZonedTime(new Date(entry.start_time), displayTimezone);
      const zonedEnd = entry.end_time ? utcToZonedTime(new Date(entry.end_time), displayTimezone) : null;
      
      const toCal = (d) => d ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes())).toISOString() : null;

      return { 
        ...entry, 
        start_time: toCal(zonedStart), 
        end_time: toCal(zonedEnd),
        // Store original UTC times for form editing (prevents double conversion)
        _original_start_time_utc: originalStartUTC,
        _original_end_time_utc: originalEndUTC,
      };
    });
  }, [calendarTimeEntries, displayTimezone, timezoneReady]);

  // --- 6. OPTIMISTIC HANDLERS (UI updates now wait for successful API responses) ---
  const handleOptimisticUpdate = useCallback(async (id, data) => {
    try {
      startTimeEntryMutation();
      // Visible entries first, then group members (other members' entries in a group
      // that is being moved or resized together).
      const entry = findEntry(id);
      if (!entry) {
        throw new Error('Entry not found');
      }

      console.log('📡 [CalendarPage] Sending update request', { id, data, teamspace_id: entry.teamspace_id });

      // Wait for API success before reflecting the change in the UI
      const response = await axios.patch(`${API_BASE_URL}teamspaces/${entry.teamspace_id}/time_entries/${id}/`, data);

      console.log('✅ [CalendarPage] Update response received', {
        id,
        status: response.status,
        statusText: response.statusText,
        data: response.data,
      });

      const updater = prev => prev.map(e => String(e.id) === String(id) ? { ...e, ...data } : e);
      setCalendarTimeEntries(updater);
      if (mayChangeGroup(entry)) refreshGroupsSoon();

      console.log('🖼️ [CalendarPage] UI updated after successful update', { id });
    } catch {
      setWsUpdateTrigger(v => v + 1);
    } finally {
      endTimeEntryMutation();
    }
  }, [findEntry, mayChangeGroup, refreshGroupsSoon, setCalendarTimeEntries, startTimeEntryMutation, endTimeEntryMutation]);

  const handleOptimisticCreate = useCallback(async (newEntryData) => {
    // Ensure teamspace_id is a number (for color mapping)
    const teamspaceId = Number(newEntryData.teamspace);
    
    // Find member_username from members array for filtering
    let memberUsername = selectedMember; // Default to selectedMember (username/email string)
    if (newEntryData.member && members.length > 0) {
      const member = members.find(m => 
        String(m.id) === String(newEntryData.member) ||
        m.email === newEntryData.member ||
        m.username === newEntryData.member
      );
      if (member) {
        memberUsername = member.username || member.member_username || member.label || member.email || selectedMember;
      }
    }
    
    // Calculate duration if not provided
    let duration = 0;
    if (newEntryData.start_time && newEntryData.end_time) {
      const start = new Date(newEntryData.start_time);
      const end = new Date(newEntryData.end_time);
      duration = Math.floor((end.getTime() - start.getTime()) / 1000); // Duration in seconds
    }
    
    // Defaults we want to apply once the API call succeeds
    const entryDefaults = {
      ...newEntryData,
      teamspace_id: teamspaceId, // Ensure it's a number for color mapping
      teamspace: teamspaceId, // Also set teamspace field for consistency
      member_username: memberUsername, // Required for filtering
      duration: duration || newEntryData.duration || 0,
      // Ensure other fields have defaults
      billable: newEntryData.billable !== undefined ? newEntryData.billable : true,
      project: newEntryData.project || null,
      category: newEntryData.category || null,
      tag: newEntryData.tag || [],
    };
    
    try {
      console.log('📡 [CalendarPage] Sending create request', { newEntryData, entryDefaults });

      // Call the API to create the entry and wait for success
      startTimeEntryMutation();
      const createdEntry = await optimisticCreateTimeEntry(newEntryData);

      console.log('✅ [CalendarPage] Create response received', {
        status: createdEntry?.status,
        data: createdEntry,
      });
      
      // Ensure the created entry has teamspace_id (API might return teamspace instead)
      const entryWithTeamspaceId = {
        ...entryDefaults,
        ...createdEntry,
        teamspace_id: createdEntry.teamspace_id || createdEntry.teamspace || teamspaceId,
        teamspace: createdEntry.teamspace || teamspaceId,
      };
      
      const adder = prev => prev.some(e => String(e.id) === String(entryWithTeamspaceId.id))
        ? prev
        : [...prev, entryWithTeamspaceId];
      setCalendarTimeEntries(adder);
      // It may complete a group with another member's identical entry.
      refreshGroupsSoon();

      console.log('🖼️ [CalendarPage] UI updated after successful create', {
        id: entryWithTeamspaceId.id,
        teamspace_id: entryWithTeamspaceId.teamspace_id,
      });

      return entryWithTeamspaceId;
    } catch (error) {
      // Trigger refresh on error; nothing to revert in the UI because we didn't update it yet
      setWsUpdateTrigger(v => v + 1);
      throw error;
    } finally {
      endTimeEntryMutation();
    }
  }, [optimisticCreateTimeEntry, setCalendarTimeEntries, refreshGroupsSoon, members, selectedMember, startTimeEntryMutation, endTimeEntryMutation]);
  
  // Handlers for EditTimeEntryForm on the calendar
  const handleCalendarTimeEntryUpdated = useCallback((updatedEntry) => {
    const entryData = updatedEntry?.time_entry || updatedEntry;
    if (!entryData?.id) return;
    const updater = prev => prev.map(e => String(e.id) === String(entryData.id) ? { ...e, ...entryData } : e);
    setCalendarTimeEntries(updater);
    if (mayChangeGroup(findEntry(entryData.id) || entryData)) refreshGroupsSoon();
  }, [setCalendarTimeEntries, findEntry, mayChangeGroup, refreshGroupsSoon]);

  const handleCalendarTimeEntryAdded = useCallback((newEntry) => {
    const entryData = newEntry?.time_entry || newEntry;
    if (!entryData?.id) return;
    const normalized = { ...entryData, teamspace_id: Number(entryData.teamspace || entryData.teamspace_id) };
    const adder = prev => prev.some(e => String(e.id) === String(normalized.id)) ? prev : [...prev, normalized];
    // Only add to the visible calendar if it matches the member filter
    const entryMemberUsername = normalized.member_username;
    const matchesFilter = !selectedMember || entryMemberUsername === selectedMember;
    if (matchesFilter) {
      setCalendarTimeEntries(adder);
    }
    // Adding a member to a group creates an entry for them; the group changed.
    refreshGroupsSoon();
  }, [setCalendarTimeEntries, selectedMember, refreshGroupsSoon]);

  const handleCalendarTimeEntryDeleted = useCallback((entryId) => {
    const remover = prev => prev.filter(e => String(e.id) !== String(entryId));
    setCalendarTimeEntries(remover);
    if (groupMap.has(String(entryId))) refreshGroupsSoon();
  }, [setCalendarTimeEntries, groupMap, refreshGroupsSoon]);

  const handleOptimisticDelete = useCallback(async (entryId) => {
    // Find the entry to get teamspace_id
    const entry = findEntry(entryId);
    if (!entry) {
      throw new Error('Entry not found');
    }
    
    try {
      console.log('📡 [CalendarPage] Sending delete request', {
        id: entryId,
        teamspace_id: entry.teamspace_id,
      });

      // Wait for the API to confirm deletion before updating the UI
      const currentUserMember = members.find(m => m.email === user?.email || m.username === user?.username);
      const deleteUrl = currentUserMember?.id
        ? `${API_BASE_URL}teamspaces/${entry.teamspace_id}/time_entries/${entryId}/?action_by=${currentUserMember.id}`
        : `${API_BASE_URL}teamspaces/${entry.teamspace_id}/time_entries/${entryId}/`;
      const response = await axios.delete(deleteUrl);

      console.log('✅ [CalendarPage] Delete response received', {
        id: entryId,
        status: response.status,
        statusText: response.statusText,
      });

      const remover = prev => prev.filter(e => String(e.id) !== String(entryId));
      setCalendarTimeEntries(remover);
      if (groupMap.has(String(entryId))) refreshGroupsSoon();

      console.log('🖼️ [CalendarPage] UI updated after successful delete', { id: entryId });
    } catch (error) {
      // Trigger refresh on error; entry remains visible in the UI
      setWsUpdateTrigger(v => v + 1);
      throw error;
    } finally {
      endTimeEntryMutation();
    }
  }, [findEntry, groupMap, refreshGroupsSoon, setCalendarTimeEntries, members, user, startTimeEntryMutation, endTimeEntryMutation]);

  // Stable, so CalendarComponent's datesSet handler keeps one identity across renders.
  // The functional update compares against the latest week rather than a render's closure,
  // and returning `prev` leaves the state untouched when the week hasn't changed.
  const handleDateRangeChange = useCallback((date) => {
    const newStartStr = format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    setWeekStartStr(prev => (prev === newStartStr ? prev : newStartStr));
  }, [setWeekStartStr]);

  if (!timezoneReady) return <Box p={2}>Loading timezone...</Box>;

  return (
    // Phones: fill the screen under the 56px mobile top bar (100vh overflowed it and
    // the mobile browser toolbar). Sideways phones: the page scrolls instead (see CalendarComponent).
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 }, height: { xs: 'calc(100dvh - 56px)', sm: '100vh' }, display: 'flex', flexDirection: 'column', [SHORT_TOUCH_MEDIA]: { height: 'auto' } }}>
      <CalendarComponent 
        timeEntries={convertedTimeEntries}
        timeEntriesLoading={timeEntriesLoading}
        // loadingAllMembers={loadingAllMembers}
        currentWeekStart={currentWeekStart}
        selectedClients={selectedClients}
        setSelectedClients={setSelectedClients}
        selectedMember={selectedMember}
        setSelectedMember={setSelectedMember}
        selectedBillable={selectedBillable}
        setSelectedBillable={setSelectedBillable}
        onDateRangeChange={handleDateRangeChange}
        onOptimisticUpdate={handleOptimisticUpdate}
        onOptimisticCreate={handleOptimisticCreate}
        onOptimisticDelete={handleOptimisticDelete}
        groupMap={groupMap}
        onTimeEntryUpdated={handleCalendarTimeEntryUpdated}
        onTimeEntryAdded={handleCalendarTimeEntryAdded}
        onTimeEntryDeleted={handleCalendarTimeEntryDeleted}
        // ... pass remaining props
        currentTimezone={displayTimezone}
        timezoneReady={timezoneReady}
        clients={clients}
        projects={allTeamspacesProjects}
        projectsByTeamspace={projectsByTeamspace}
        members={allTeamspacesMembers}
        teamspaces={teamspaces}
      />
    </Box>
  );
};

export default CalendarPage;